// pageInside/payments/paymentMethodsLogic.js
//
// Pure rules shared by the Checkout Modal and the Wallet (unit-tested):
//   method shown = allowed by the Family (server, already AND-ed with
//   provider configuration) AND technically available for THIS transaction
//   (device wallet, PayPal SDK eligibility, order type).
// Stripe is always first and is the default selection.

// Buyer-facing priority. Card remains available whenever Stripe is ready,
// but a genuinely usable wallet is preferred without requiring a saved card.
export const CHECKOUT_METHOD_ORDER = Object.freeze(["googlePay", "paypal", "applePay", "stripe"]);

/**
 * @param {{ stripe?: boolean, paypal?: boolean, googlePay?: boolean, applePay?: boolean }} allowed
 * @param {{ paypal?: boolean, googlePay?: boolean, applePay?: boolean }} available device / SDK / order availability
 * @returns {string[]} methods to offer, Stripe first
 */
export const displayableCheckoutMethods = (allowed = {}, available = {}) =>
  CHECKOUT_METHOD_ORDER.filter((method) => {
    if (method === "stripe") return allowed.stripe !== false; // primary method, always offered
    return allowed[method] === true && available[method] === true;
  });

/** The selector row is shown ONLY when there is a real choice. */
export const shouldShowMethodSelector = (methods = []) => methods.length > 1;

/** Keep the current choice if still offered; otherwise use the highest-priority ready method. */
export const resolveSelectedMethod = (current, methods = []) =>
  methods.includes(current) ? current : (methods[0] || null);

/** Shared Product Preview / checkout-entry decision. Order creation itself
 * remains separate from immediate provider payment eligibility. */
export const resolveCheckoutEntryAction = ({
  loading = false, providerError = false, methods = [], stripeReady = false,
  cardsReady = false, hasCards = false, selectedCardUsable = false,
} = {}) => {
  if (loading || (!cardsReady && stripeReady)) return "loading";
  const wallets = methods.filter((method) => method !== "stripe");
  if (wallets.length) return "checkout";
  if (stripeReady && !hasCards) return "add_card";
  if (stripeReady && !selectedCardUsable) return "select_card";
  if (stripeReady) return "checkout";
  return providerError ? "technical_error" : "payment_unavailable";
};

/**
 * Withdrawal method cases (spec §11):
 *   A bank only -> no choice, bank form      B both -> choice, bank default
 *   C PayPal only -> PayPal form directly    D none -> withdrawals unavailable
 */
export const withdrawalMethodState = (withdrawal = {}) => {
  const methods = [];
  if (withdrawal.bankTransfer === true) methods.push("bank");
  if (withdrawal.paypal === true) methods.push("paypal");
  return {
    methods,
    showChoice: methods.length > 1,
    defaultMethod: methods[0] || null,
    unavailable: methods.length === 0,
  };
};

/** Dollars typed by the user -> integer cents (never floating math on money). */
export const parseAmountToCents = (value) => {
  const text = String(value ?? "").trim().replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  return Number(whole) * 100 + Number((fraction + "00").slice(0, 2));
};

export const formatCents = (cents, currency = "USD") => {
  const value = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: String(currency || "USD").toUpperCase() }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
};
